import React from 'react';
import {FormattedMessage} from 'react-intl';
import moment from 'moment/moment';
import Collapsible from '../Collapsible';
import InspectionStatus from '../InspectionStatus';
import Unit from "../../../_common/components/Unit";
import UnitItem from "../UnitItem/UnitItem";
import {InspectionStatusProvider} from '../InspectionStatusContext';
import {getEstimatePrice} from '../../utils/getEstimatePrice';
import {getPaidPartValue} from '../../utils/getPaidPartValue';
import {getElevator} from '../../utils/getElevator';
import {getPaidColor} from '../../../_common/utils/getPaidColor';
import {toAbsoluteUrl} from "../../../../../_metronic/_helpers";
import {useInspectionsPageUIContext} from "../InspectionsPageUIContext";
import styles from './styles.module.scss';

export function GeneralColumnFormatter(cellContent) {
  const {expandAll} = useInspectionsPageUIContext();
  const {
    firmShumaID,
    createTime,
    shumaID, shumaStatusTypeID, shumaStatusTypeName,
    customers,
    cityName, streetName, houseNumber,
    gush, helka, ttHelka,
    shumaTypeName, originTypeName,
    propertyTypeName,
    purchaseCompensation,
    monthlyRent,
    registeredArea,
    roomsCount,
    floor,
    elevator,
    userFirstName, userLastName,
    shumaDueDate,
    paid, totalPayment,
    shumaTypeID,
    originTypeID,
    estimatePriceFinalRounded,
    finalMarketPrice,
    propertyPriceMinusDepreciation,
    estimatePriceFinal,
    generalNoteStatusTypeName
  } = cellContent;
  const address = [
    [cityName, streetName].filter(Boolean).join(', '),
    houseNumber
  ].filter(Boolean).join(' ');
  const gushHelka = [gush, helka, ttHelka].filter(Boolean).join('/');
  const {paidPart, paidPartPercent} = getPaidPartValue(paid, totalPayment)
  const fullName = [userFirstName, userLastName].filter(Boolean).join(' ');
  const prefix = 'inspections-column';

  return (
    <Collapsible
      expandAll={expandAll}
      main={
        <div className={styles.mainContainer}>
          <Unit
            icon={'/media/common/Clipboard_navy.svg'}
            text={<FormattedMessage id={`${prefix}-general`} defaultMessage={'General'}/>}
          >
            <UnitItem
              icon={'/media/common/Doc_primary.svg'}
              label={<FormattedMessage id={`${prefix}-firmShumaID`} defaultMessage={'Firm shuma ID'}/>}
              value={firmShumaID}
            />
            <UnitItem
              icon={'/media/common/Date_primary.svg'}
              label={<FormattedMessage id={`${prefix}-create-date`} defaultMessage={'Create date'}/>}
              value={createTime ? moment(createTime).format('DD-MM-YYYY') : ''}
            />
            <UnitItem
              icon={'/media/common/User_primary.svg'}
              label={<FormattedMessage id={`${prefix}-fullName`} defaultMessage={'FullName'}/>}
              value={fullName}
            />
            <UnitItem
              icon={'/media/common/Check_circle_primary.svg'}
              label={<FormattedMessage id={`${prefix}-status`} defaultMessage={'Status'}/>}
              value={
                <InspectionStatusProvider
                  shumaID={shumaID}
                  shumaStatusTypeID={shumaStatusTypeID}
                  shumaStatusTypeName={shumaStatusTypeName}
                >
                  <InspectionStatus/>
                </InspectionStatusProvider>
              }
            />
            <UnitItem
              icon={'/media/common/Notes_primary.svg'}
              label={<FormattedMessage id={`${prefix}-lastNote`} defaultMessage={'Note'}/>}
              value={generalNoteStatusTypeName}
            />
            <UnitItem
              icon={'/media/common/Pie_primary.svg'}
              label={<FormattedMessage id={`${prefix}-paid`} defaultMessage={'Paid'}/>}
              value={paidPartPercent}
              customStyles={{color: getPaidColor(paidPart), direction: 'ltr'}}
            />
          </Unit>
          <Unit
            icon={'/media/common/Geo_red.svg'}
            text={<FormattedMessage id={`${prefix}-lot`} defaultMessage={'Lot'}/>}
          >
            <UnitItem
              icon={'/media/common/Address_primary.svg'}
              label={<FormattedMessage id={`${prefix}-address`} defaultMessage={'Address'}/>}
              value={address}
            />
            <UnitItem
              icon={'/media/common/Map_primary.svg'}
              label={<FormattedMessage id={`${prefix}-gush-helka`} defaultMessage={'Gush'}/>}
              value={gushHelka}
            />
            <UnitItem
              icon={'/media/common/Money_primary.svg'}
              label={<FormattedMessage id={`${prefix}-estimate-price`} defaultMessage={'Estimate price'}/>}
              value={getEstimatePrice(
                shumaTypeID,
                originTypeID,
                estimatePriceFinalRounded,
                finalMarketPrice,
                propertyPriceMinusDepreciation,
                estimatePriceFinal
              ).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}
            />
            <UnitItem
              icon={'/media/common/Hands_primary.svg'}
              label={<FormattedMessage id={`${prefix}-purchaseCompensation`} defaultMessage={'Purchase compensation'}/>}
              value={purchaseCompensation}
            />
            <UnitItem
              icon={'/media/common/Calculator_primary.svg'}
              label={<FormattedMessage id={`${prefix}-monthlyRent`} defaultMessage={'Monthly rent'}/>}
              value={monthlyRent ? (monthlyRent).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : ''}
            />
          </Unit>
          <Unit
            icon={'/media/common/Group_pink.svg'}
            text={<FormattedMessage id={`${prefix}-customers`} defaultMessage={'Customers'}/>}
          >
            <div className={styles.customersContainer}>
              {Array.isArray(customers)
                && customers.map((item, index) => (
                <div className={styles.selectedItem} key={index}>
                  <div className={styles.line}>
                    <img src={toAbsoluteUrl(`/media/common/${item?.greenInvoiceClientID 
                      ? 'GI-logo.svg' : 'User_primary.svg'}`)} alt="i"/>
                    <span>{[item?.firstName, item?.lastName].filter(Boolean).join(' ') || '-'}</span>
                  </div>
                  <div className={styles.line}>
                    <img src={toAbsoluteUrl('/media/common/Id-badge_primary.svg')} alt="i"/>
                    <span>{item?.idNumber || '-'}</span>
                  </div>
                  <div className={styles.line}>
                    <img src={toAbsoluteUrl('/media/common/Phone_primary.svg')} alt="i"/>
                    <span>{item?.phone || '-'}</span>
                  </div>
                  <div className={styles.line}>
                    <img src={toAbsoluteUrl('/media/common/Email_primary.svg')} alt="i"/>
                    <span>{item?.email || '-'}</span>
                  </div>
                </div>
                ))
              }
            </div>
          </Unit>
        </div>
      }
    >
      <div className={styles.collapseContainer}>
        <Unit
          icon={'/media/common/Decision-tree_orange.svg'}
          text={<FormattedMessage id={`${prefix}-info`} defaultMessage={'Info'}/>}
        >
          <UnitItem
            icon={'/media/common/Type_primary.svg'}
            label={<FormattedMessage id={`${prefix}-shuma-type`} defaultMessage={'Type'}/>}
            value={shumaTypeName}
          />
          <UnitItem
            icon={'/media/common/Arrow-left_primary.svg'}
            label={<FormattedMessage id={`${prefix}-shuma-origin`} defaultMessage={'Origin'}/>}
            value={originTypeName}
          />
          <UnitItem
            icon={'/media/common/Home_primary.svg'}
            label={<FormattedMessage id={`${prefix}-property-type`} defaultMessage={'Property type'}/>}
            value={propertyTypeName}
          />
          <UnitItem
            icon={'/media/common/Date_primary.svg'}
            label={<FormattedMessage id={`${prefix}-due-date`} defaultMessage={'Due date'}/>}
            value={shumaDueDate ? moment(shumaDueDate).format('DD-MM-YYYY') : ''}
          />
        </Unit>
        <Unit
          icon={'/media/common/Layers_green.svg'}
          text={<FormattedMessage id={`${prefix}-additional`} defaultMessage={'Additional'}/>}
        >
          <UnitItem
            icon={'/media/common/Key_primary.svg'}
            label={<FormattedMessage id={`${prefix}-roomsCount`} defaultMessage={'Rooms count'}/>}
            value={roomsCount}
          />
          <UnitItem
            icon={'/media/common/Stairs_primary.svg'}
            label={<FormattedMessage id={`${prefix}-floor`} defaultMessage={'Floor'}/>}
            value={floor}
          />
          <UnitItem
            icon={'/media/common/Elevator_primary.svg'}
            label={<FormattedMessage id={`${prefix}-elevator`} defaultMessage={'Elevator'}/>}
            value={getElevator(elevator)}
          />
          <UnitItem
            icon={'/media/common/Lock_primary.svg'}
            label={<FormattedMessage id={`${prefix}-registeredArea`} defaultMessage={'Registered area'}/>}
            value={registeredArea}
          />
        </Unit>
      </div>
    </Collapsible>
  );
}